import { useSessionStorage } from '@vueuse/core'
import { CustomerType, ProfessionalCustomerGroupId } from '~/types/general'
import type { RequestParameters } from '#shopware'

export type RegistrationAddress = {
  company?: string
  website?: string | undefined
  street: string
  postOfficeBox?: string | undefined
  zipCode: string
  city: string
  country: {
    id: string
    code: string | null
  }
  businessDescription?: string
  role?: string
  department?: string
  salutation?: string
  firstName?: string
  lastName?: string
  language?: {
    id: string
    code: string | null
  }
  phone?: string
  email?: string
  password?: string
  passwordRepeat?: string
}

export type State = {
  type: CustomerType
  existingCustomer: boolean | null
  customerNumber: string | null
  pdf: {
    name: string
    base64: string
  } | null
  address: RegistrationAddress
  addressValidated: boolean
  acceptedTerms: boolean
  subscribedNewsletter: boolean
  interestedInBrands: boolean | null
  selectedBrands: string[]
}

export const useRegistrationStore = defineStore('registration', () => {
  const state = useSessionStorage<State>('registration', {
    type: CustomerType.Professional,
    existingCustomer: null,
    customerNumber: null,
    pdf: null,
    address: {},
    addressValidated: false,
    acceptedTerms: false,
    subscribedNewsletter: true,
    interestedInBrands: null,
    selectedBrands: [],
  })

  const addressErrors = ref<Record<string, string>>({})

  const verified = computed(() => {
    return (
      state.value.existingCustomer === false ||
      (state.value.customerNumber !== null && state.value.existingCustomer)
    )
  })

  const activeStep = computed(() => {
    if (!verified.value) {
      return 1
    }
    if (!state.value.addressValidated) {
      return 2
    }
    return 3
  })

  const reset = () => {
    state.value = {
      type: state.value.type,
      existingCustomer: null,
      customerNumber: null,
      pdf: null,
      address: {},
      addressValidated: false,
      acceptedTerms: false,
      subscribedNewsletter: true,
      interestedInBrands: null,
      selectedBrands: [],
    }
  }

  const registrationData = computed(
    (): RequestParameters<'register'> & {
      customApplicationData: {
        customerNumber: string | null
        businessDescription?: string
        website?: string
        role?: string
        language?: {
          id: string
          code: string | null
        }
        country?: {
          id: string
          code: string | null
        }
        newsletter: boolean
        brands: string[]
        certificate?: string
      }
    } => {
      const accountType =
        state.value.type === CustomerType.Professional ? 'business' : 'private'

      // used to request permissions to professional customer group,
      // '06b4423e9d636aac1fb52f0207203bc6' === md5('Professionals')
      const requestedGroupId =
        state.value.type === CustomerType.Professional
          ? ProfessionalCustomerGroupId
          : null

      const address = state.value.address as Required<RegistrationAddress>

      return {
        accountType,
        requestedGroupId,
        acceptedDataProtection: state.value.acceptedTerms,
        guest: false,
        birthdayDay: 1,
        birthdayMonth: 1,
        birthdayYear: 1970,
        email: address.email,
        firstName: address.firstName,
        lastName: address?.lastName,
        password: address?.password,
        billingAddress: {
          salutationId: address?.salutation,
          company: address.company,
          department: address.department,
          street: address.street,
          zipcode: address.zipCode,
          city: address.city,
          countryId: address.country?.id ?? null,
          phoneNumber: address.phone,
          additionalAddressLine1: address.postOfficeBox,
        },
        shippingAddress: {
          salutationId: address?.salutation,
          company: address.company,
          department: address.department,
          firstName: address.firstName,
          lastName: address.lastName,
          street: address.street,
          zipcode: address.zipCode,
          city: address.city,
          countryId: address.country?.id ?? null,
          phoneNumber: address.phone,
          additionalAddressLine1: address.postOfficeBox,
        },
        customApplicationData: {
          customerNumber: state.value.customerNumber,
          businessDescription: address.businessDescription,
          website: address.website,
          role: address.role,
          language: address.language,
          country: address.country,
          newsletter: state.value.subscribedNewsletter,
          brands: state.value.selectedBrands,
          certificate: state.value.pdf?.base64,
        },
      }
    }
  )

  return { state, activeStep, registrationData, reset, addressErrors }
})
